import React from 'react';
import styled from 'styled-components';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import _ from 'lodash';
import * as TYPES from '@contentful/rich-text-types';
import { Link as AnchorLink } from 'react-scroll';
import { direction } from 'direction';
import { GatsbyImage } from 'gatsby-plugin-image';

import Video from '../basic/Video';
import Text from '../basic/Text';
import SmallCallout from '../digital-guide/SmallCallout';
import theme from '../../styles/theme';
import BasicLink from '@basic/BasicLink';
import BasicButton from '@basic/BasicButton';
import Table from '../basic/GoogleSheetTable';
import SingleQuote from '../basic/SingleQuote';
import QuoteWithBackground from '../basic/QuoteWithBackground';
import findNested from '../basic/helper-functions/findNestedValue';
import HTMLWrapper from '../basic/HTMLWrapper';

const {
	colors,
	breakPoints: { screenQuery },
} = theme;

const ImageWrapper = styled.div`
	width: ${props =>
		props.imageWrap === 'Wrap left' || props.imageWrap === 'Wrap right'
			? '50%'
			: '100%'};
	float: ${props => {
		if (props.imageWrap === 'Wrap left') {
			return 'left';
		} else if (props.imageWrap === 'Wrap right') {
			return 'right';
		} else 'none';
	}};
	height: 100%;
	background-color: #333;
	margin: ${props => {
		if (props.imageWrap === 'Wrap left') {
			return '0 20px 0 0';
		} else if (props.imageWrap === 'Wrap right') {
			return '0 0 0 20px';
		} else '60px auto';
	}};
	img {
		max-width: 100%;
		max-height: 100%;
		width: 100%;
	}
	:first-child {
		margin-top: 0;
	}
`;

const VideoWrapper = styled.div`
	margin: 30px auto;
`;

const Caption = styled(Text)`
	background-color: ${colors.black};
	color: ${colors.white} !important;
	text-align: left;
	letter-spacing: 0.55px;
	/* overrides auto padding from <HTMLWrapper>: */
	margin: 0px auto !important;
	padding: 14px;
`;

const Quote = styled(SingleQuote)`
	.quote-content {
		margin-bottom: 35px;
	}
`;
const ListWrapper = styled.div`
	border: 5px solid ${colors.secondary.main};
	padding: 30px;
	padding-bottom: 0;
	margin: 60px 0;
`;

const List = styled.ul`
	display: flex;
	flex-wrap: wrap;
	li {
		/* width: calc(50% - 15px) !important; */
		margin-bottom: 30px !important;
		padding-left: 2em !important;
		:before {
			margin-left: -1em !important;
			color: ${colors.secondary.main} !important;
		}
	}
	li:nth-child(even) {
		margin-left: 15px;
	}
	li:nth-child(odd) {
		margin-right: 15px;
	}
	${screenQuery.small} {
		li {
			width: 100% !important;
			margin: 0 0 20px !important;
		}
	}
`;

const BasicTable = styled.table`
	table-layout: auto;
	width: 100%;
	margin: 20px 0;

	tr {
		th {
			background-color: #e7f7f9;
			padding: 20px;
		}

		td {
			padding: 20px;
			border-bottom: 1px solid #333;

			&:not(:last-child) {
				border-right: 1px solid #a6aab1;
			}
		}
	}
`;

const options = moreOptions => {
	// this shit wild: https://github.com/gatsbyjs/gatsby/blob/master/packages/gatsby-source-contentful/CHANGELOG.md#400-next0-2020-11-09
	return {
		// BEFORE YOU START PULLING YOUR HAIR OUT AS TO WHY AN EMBEDDED ASSET OR ENTRY IS NOT SHOWING UP,
		// CLEAR YOUR GATSBY CACHE (gatsby clean) AND RESTART YOUR SERVER.
		// AND ENSURE THAT CONTENFUL_ID IS ON YOUR GRAPHQL QUERY
		renderNode: {
			[TYPES.BLOCKS.PARAGRAPH]: (node, children) => {
				if (moreOptions && moreOptions.isCodeBlock) {
					return <HTMLWrapper html={node.content[0].value} />;
				} else {
					return direction(children) === 'rtl' ? (
						<p style={{ direction: 'rtl', textAlign: 'right' }}>{children}</p>
					) : (
						<p>{children}</p>
					);
				}
			},
			[TYPES.BLOCKS.TABLE]: (node, children) => {
				return <BasicTable>{children}</BasicTable>;
			},
			[TYPES.INLINES.HYPERLINK]: node => {
				if (node.data.uri.indexOf('google_sheet') >= 0) {
					return (
						<Table
							id={node.data.uri.slice(
								node.data.uri.indexOf('=') + 1,
								node.data.uri.length
							)}
						/>
					);
				}
				const linkText = node.content.map(c => c.value).join(' ');

				if (moreOptions?.openInNewTab) {
					return (
						<a href={node.data.uri} target="_blank">
							{linkText}
						</a>
					);
				}

				if (moreOptions?.arrowLink) {
					return (
						<BasicButton
							arrow
							secondary
							to={node.data.uri}
							color={colors.brandMain}
						>
							{linkText}
						</BasicButton>
					);
				} else {
					//if there's a space at the beginning of the link b/c of content editor oversight:
					return <BasicLink to={node.data.uri}>{linkText}</BasicLink>;
				}
			},
			[TYPES.INLINES.ASSET_HYPERLINK]: node => {
				const linkText = node.content[0].value;
				const link = node.data.target.file.url;

				return <BasicLink to={link}>{linkText}</BasicLink>;
			},
			[TYPES.INLINES.EMBEDDED_ENTRY]: node => {
				const content = node.data.target;

				if (content?.type === 'ContentfulQuoteWithBackgroundColor') {
					return (
						<QuoteWithBackground
							quoteAuthor={content.quoteAuthor}
							quoteBody={content.quoteBody.quoteBody}
							sectionColor={moreOptions?.sectionColor}
						/>
					);
				}

				if (content?.__typename === 'ContentfulBasicRichText') {
					if (content?.isCodeBlock) {
						const parsedJSON = JSON.parse(content.richText.raw).content;
						const codeSnippet = _.find(parsedJSON, el =>
							el.nodeType.includes('paragraph')
						)?.content[0]?.value;

						return <HTMLWrapper html={codeSnippet} />;
					}
				}

				if (content?.quote) {
					const quoteColor = _.sample(['blue', 'yellow', 'green', 'orange']);
					if (content.quoteSourceBioPhoto) {
						return (
							<Quote
								quoteImageColor={quoteColor}
								quote={content.quote.quote}
								source={content.quoteSource}
								title={content.quoteSourceJobTitle}
								bioImage={content.quoteSourceBioPhoto.file.url}
								textAlign="left"
							/>
						);
					}
				}

				if (content?.type === 'ContentfulElementImageBlock') {
					return (
						<ImageWrapper imageWrap={content.imageWrap2}>
							<img src={content?.image.file.url} alt={content?.imageCaption} />
							{!content?.imageCaption ? null : (
								<Caption color="white" component="b4">
									{content?.imageCaption}
								</Caption>
							)}
						</ImageWrapper>
					);
				}

				if (content?.type === 'ContentfulAnchorLink') {
					return (
						<AnchorLink
							to={content?.linkId}
							smooth={true}
							offset={content?.offset}
							duration={500}
						>
							{content?.linkText}
						</AnchorLink>
					);
				}
			},
			[TYPES.BLOCKS.EMBEDDED_ASSET]: node => {
				const image = node.data.target;

				return (
					image && (
						<ImageWrapper>
							{image?.gatsbyImageData ? (
								<GatsbyImage image={image.gatsbyImageData} alt={image?.title} />
							) : (
								<img src={image?.file.url} alt={image?.title} />
							)}
							{moreOptions?.noCaptions ? null : (
								<Caption color="white" component="b4">
									{image?.title}
								</Caption>
							)}
						</ImageWrapper>
					)
				);
			},
			[TYPES.BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
				const content = node.data.target;
				// TODO: Refactor old content rendering to use Typename check for consistency & to ensure we're not redering components with same field names wrong.
				if (content?.videoLink) {
					return (
						<VideoWrapper className="embedded-video">
							<Video
								url={content.videoLink}
								thumbnail={
									content.videoThumbnail
										? content.videoThumbnail.file.url
										: null
								}
							/>
						</VideoWrapper>
					);
				} else if (content?.isCodeBlock) {
					const parsedJSON = JSON.parse(content.richText.raw).content;
					const codeSnippet = _.find(parsedJSON, el =>
						el.nodeType.includes('paragraph')
					)?.content[0]?.value;

					return <HTMLWrapper html={codeSnippet} />;
				} else if (
					content?.richText &&
					content.richText.raw.indexOf(TYPES.BLOCKS.LIST_ITEM) > 0
				) {
					const parsedJSON = JSON.parse(content.richText.raw).content;

					const header = _.find(parsedJSON, el =>
						el.nodeType.includes('heading')
					)?.content[0]?.value;
					const list = _.find(parsedJSON, el =>
						el.nodeType.includes('list')
					)?.content?.map(li => findNested(li, 'value')[0]);
					return (
						<ListWrapper>
							{header && (
								<Text
									textAlign="center"
									component="h3"
									style={{ marginBottom: 30 }}
								>
									{header}
								</Text>
							)}
							{list && (
								<List>
									{list.map(item => (
										<li>{item}</li>
									))}
								</List>
							)}
						</ListWrapper>
					);
				} else if (content?.quote) {
					const quoteColor = _.sample(['blue', 'yellow', 'green', 'orange']);

					return (
						<Quote
							quoteImageColor={quoteColor}
							quote={content.quote.quote}
							source={content?.quoteSource || content?.quoteAuthor?.authorName}
							title={
								content?.quoteSourceJobTitle ||
								content?.quoteAuthor?.authorJobTitle
							}
							bioImage={
								content?.quoteSourceBioPhoto?.file.url ||
								content?.quoteAuthor?.authorBioPhoto?.file.url
							}
							textAlign="left"
							className="quote-wrapper"
						/>
					);
				} else if (content?.type === 'ContentfulQuoteWithBackgroundColor') {
				} else if (content?.type === 'ContentfulSmallCalloutBlock') {
					return (
						<SmallCallout
							additionalText={content.additionalText}
							smallCalloutSubtitle={
								content.smallCalloutSubtitle.smallCalloutSubtitle
							}
							smallCalloutTitle={content.smallCalloutTitle}
						/>
					);
				} else if (content?.type === 'ContentfulElementImageBlock') {
					return (
						<ImageWrapper imageWrap={content.imageWrap2}>
							<img src={content?.image.file.url} alt={content?.imageCaption} />
							{!content?.imageCaption ? null : (
								<Caption color="white" component="b4">
									{content?.imageCaption}
								</Caption>
							)}
						</ImageWrapper>
					);
				} else {
					return;
				}
			},
		},
	};
};

export default (richText, moreOptions) => {
	// if there is something like "{ replaceNode: { 'heading-2': "heading-4" } }"
	// in moreOptions, this method switches those node types before passing the json
	// to the documentToReactComponents:
	if (typeof moreOptions?.replaceNode === 'object') {
		const { replaceNode } = moreOptions;
		for (const property in replaceNode) {
			// with regex:
			const re = new RegExp(property, 'g');
			richText.raw = richText?.raw?.replace(re, replaceNode[property]);
		}
	}

	return renderRichText(richText, options(moreOptions));
};
